/**
 * Release Notes Service
 * Reads the published release notes behind the "What's New" page (/whats-new).
 *
 * Access is enforced in the database (migration 2026-09-24-009): an active internal profile can
 * read PUBLISHED notes; drafts are visible only to an author (superadmin/admin). This module does
 * not — and cannot — relax that.
 */

import { supabase } from "./supabase";
import type { ReleaseNote, ReleaseNoteGroup } from "../types/releaseNote.types";

const COLUMNS =
  "id, version, slug, title, summary, category, audience, status, published_at, sort_order, created_at, updated_at";

/**
 * Every published note, newest release first.
 *
 * ⚠️ `.eq("status", "published")` is here on purpose and is NOT redundant with the row-level
 * rule. An author (superadmin/admin) is allowed to read DRAFT rows, so without this filter the
 * page would show an unfinished note to exactly the people most likely to be looking at it.
 * The page is the reader's view of the product; drafting happens elsewhere.
 *
 * ⚠️ Explicit `.limit()`: an unbounded PostgREST select silently caps at 1000 rows, which reads
 * as "that's all there is" rather than as a truncation. 500 releases-worth of notes is far beyond
 * anything this page will hold, and the bound is stated rather than inherited.
 */
export async function getPublishedReleaseNotes(): Promise<ReleaseNote[]> {
  const { data, error } = await supabase
    .from("release_notes")
    .select(COLUMNS)
    .eq("status", "published")
    .order("published_at", { ascending: false, nullsFirst: false })
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false })
    .limit(500);

  if (error) {
    console.error("Error fetching release notes:", error);
    throw error;
  }

  return (data || []) as ReleaseNote[];
}

/**
 * Group the flat list by release, preserving the order the query returned.
 *
 * Kept as a pure function (and exported) so the grouping can be unit-tested without a browser or
 * a database: it is the only real logic on this page.
 */
export function groupReleaseNotes(notes: ReleaseNote[]): ReleaseNoteGroup[] {
  const groups: ReleaseNoteGroup[] = [];
  const byVersion = new Map<string, ReleaseNoteGroup>();

  for (const note of notes) {
    let group = byVersion.get(note.version);
    if (!group) {
      group = {
        version: note.version,
        published_at: note.published_at,
        notes: [],
      };
      byVersion.set(note.version, group);
      groups.push(group);
    }
    // A release's heading date is the newest date inside it, in case two notes in one version
    // were published a day apart.
    if (
      note.published_at &&
      (!group.published_at || note.published_at > group.published_at)
    ) {
      group.published_at = note.published_at;
    }
    group.notes.push(note);
  }

  return groups;
}
